import React, { useMemo } from "react";
import styles from "../styles/vinos.module.css";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import { showSuccessToast } from "../../../components/toast/toastBus";

import { useVinosData } from "../hooks/useVinosData";
import { useVinosUI } from "../hooks/useVinosUI";
import { useVinoForm } from "../hooks/useVinoForm";
import { useVinoSelection } from "../hooks/useVinoSelection";

import { filterAndSortVinos } from "../domain/vinosFilters";
import { vinosApi } from "../api/vinosApi";

import VinosFiltersBar from "../components/VinosFiltersBar";
import VinosActionsBar from "../components/VinosActionsBar";
import VinosList from "../components/VinosList";
import VinoModal from "../components/VinoModal";

export default function Vinos() {
  const { vinos, loading, error, reload } = useVinosData();
  const ui = useVinosUI();
  const form = useVinoForm();
  const sel = useVinoSelection();

  const list = useMemo(
    () => filterAndSortVinos(vinos, ui.q, ui.sortBy),
    [vinos, ui.q, ui.sortBy]
  );

  const requestDelete = (id) => {
    ui.setDeleteId(id);
    ui.setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!ui.deleteId) return;
    try {
      await vinosApi.remove(ui.deleteId);
      await reload();
    } catch (e) {
      console.error(e);
      alert("Error al eliminar el vino.");
    } finally {
      ui.setShowConfirm(false);
      ui.setDeleteId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const esEdicion = !!form.editingVino;
      if (esEdicion) {
        await vinosApi.update({ id: form.editingVino.id, ...form.formData });
      } else {
        await vinosApi.create(form.formData);
      }
      form.close();
      form.reset();
      await reload();
      showSuccessToast(esEdicion ? "Vino actualizado con éxito" : "Vino creado con éxito");
    } catch (e2) {
      console.error(e2);
      alert("Error al guardar el vino.");
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={`${styles.card} ${styles.soft}`}>Cargando vinos…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={`${styles.card} ${styles.warn}`}>
            <strong>Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.wrap} ${styles.stack}`}>
        <div className={styles.titlebar}>
          <h2>Vinos</h2>
        </div>

        <VinosFiltersBar
          q={ui.q}
          onQ={ui.setQ}
          sortBy={ui.sortBy}
          onSortBy={ui.setSortBy}
        />

        <VinosActionsBar
          selection={sel.selection}
          autoPrint={ui.autoPrint}
          onClearSelection={sel.clearSelection}
          onCreate={form.openCreate}
        />

        <VinosList
          list={list}
          isOpen={ui.isVinoOpen}
          onToggle={ui.toggleVino}
          selection={sel.selection}
          onToggleStage={sel.toggleStage}
          onEdit={form.openEdit}
          onDelete={requestDelete}
        />
      </div>

      <VinoModal
        open={form.formVisible}
        editing={!!form.editingVino}
        formData={form.formData}
        setFormData={form.setFormData}
        onClose={() => {
          form.close();
          form.reset();
        }}
        onSubmit={handleSubmit}
      />

      {ui.showConfirm && (
        <ConfirmDeleteModal
          title="Confirmar eliminación"
          message="Esta acción eliminará el vino seleccionado."
          confirmWord="Eliminar"
          onClose={() => {
            ui.setShowConfirm(false);
            ui.setDeleteId(null);
          }}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
